import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { Column } from '@orion-platform/datagrid';
import { environment } from '@orion/environment';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { Observable } from 'rxjs';
import { EnumStocksFieldStructure, StocksListField } from '../models/stocks-list.model';
import { STOCKS_COLUMN_LIST, STOCKS_FIELDS_KEY } from '../pages/stocks-list/stocks-list.consts';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { DataGridResult } from '../store/partnumbers-list/partnumbers-list.types';
import { Stock, StockResult, StocksDetails } from '../store/stocks-list/stocks-list.types';
import { Plants } from '../types';
import { ColumnsState } from '../types/columns.types';
import { formatParams, getUrl } from '../utils/general.utils';
import { ColumnsService } from './columns.service';
import { DefaultFormSettingsService } from './default-form-settings.service';

@Injectable({
  providedIn: 'root',
})
export class StocksListService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly columns = inject(ColumnsService);

  /**
   * The grid's columns: the field structure this plant is allowed, merged with
   * whatever the user saved.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new StocksListField(EnumStocksFieldStructure.StocksTable, formFields).fields as Column[],
      columnListKey: STOCKS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(STOCKS_FIELDS_KEY),
    });

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  private readonly orchPlants: ReadonlySet<string> = new Set([
    Plants.MULTI,
    Plants.JUSDA,
    Plants.CTP,
  ]);

  getStocksList(data: ApiQueryParams): Observable<DataGridResult<StocksDetails>> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    const params = formatParams(data);
    return this.http.get<DataGridResult<StocksDetails>>(url, { params });
  }

  createStock(stock: Stock): Observable<StockResult> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.http.post<StockResult>(url, stock);
  }

  editStock(stock: Stock): Observable<StockResult> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.http.put<StockResult>(`${url}/${stock.id}`, stock);
  }

  deleteStock(id: string): Observable<StockResult> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.http.delete<StockResult>(`${url}/${id}`);
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    const url = getUrl(this.baseUrl, this.mm.carriers);
    return this.defaultForms.getFields(url, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.defaultForms.formKeys(url);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.defaultForms.getFormSettings(url, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    const url = getUrl(this.baseUrl, this.mm.stocks);
    return this.defaultForms.update(url, formKey, payload);
  }

  getWarehouseList(plant: string): Observable<string[]> {
    const base = getUrl(this.baseUrl, this.mm.stocks);
    const apiSuffix = this.getApiSuffixForPlant(plant);
    return this.http.get<string[]>(`${base}${apiSuffix}/Buildings`);
  }

  private getApiSuffixForPlant(plant: Plants | string): string {
    return this.orchPlants.has(String(plant ?? '').toUpperCase()) ? 'Orch' : '';
  }

  /**
   * Which fields the create form shows, as the user arranged them.
   *
   * The same list in the same shape as a grid's columns, stored the same way —
   * only what draws it differs.
   * @returns {Observable<ColumnsState>} The fields, and whether they are still the defaults.
   */
  getFormFieldColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new StocksListField(EnumStocksFieldStructure.StocksCreateForm, formFields)
          .fields as unknown as Column[],
      columnListKey: 'stocks_field_list',
      formFields$: this.getDefaultFormSettingFields('STOCKS_DETAIL_FORMKEY'),
    });
}
